import React, { useState } from 'react';
import { Send, Trash2, MessageSquare } from 'lucide-react';
import devIssueService from '../../../services/devIssueService';

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

const IssueComments = ({ issueId, comments, onCommentsChange, currentUserEmail }) => {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      const res = await devIssueService.addComment(issueId, text.trim());
      onCommentsChange([...comments, res.data]);
      setText('');
    } catch {
      // silently fail — parent shows errors
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await devIssueService.deleteComment(issueId, commentId);
      onCommentsChange(comments.filter(c => c.id !== commentId));
    } catch {
      // silently fail
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="h-4 w-4 text-gray-500" />
        <h4 className="text-sm font-semibold text-gray-700">
          Comments {comments.length > 0 && <span className="text-gray-400 font-normal">({comments.length})</span>}
        </h4>
      </div>

      <div className="space-y-3 mb-4">
        {comments.length === 0 && (
          <p className="text-sm text-gray-400 italic">No comments yet.</p>
        )}
        {comments.map(c => (
          <div key={c.id} className="group bg-gray-50 rounded-lg p-3 border border-gray-100">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-yellow-700 text-xs font-bold">
                    {(c.user_email || '?').charAt(0).toUpperCase()}
                  </span>
                </div>
                <span className="text-xs font-medium text-gray-600">{c.user_email}</span>
                <span className="text-xs text-gray-400">{formatDate(c.created_at)}</span>
              </div>
              <button
                onClick={() => handleDelete(c.id)}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{c.comment_text}</p>
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="flex gap-2">
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit(e); }}
          placeholder="Add a comment… (Ctrl+Enter to submit)"
          rows={2}
          className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
        />
        <button
          type="submit"
          disabled={!text.trim() || submitting}
          className="self-end px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
};

export default IssueComments;
